import strings from "global/constants/StringConstants";
import {
  fillCopy,
  getErrorMessageText,
  handleErrorMessage,
  openErrorNotification,
} from "helpers/methods";
import { getApiStatus } from "utils/apiStatus";
import {
  IdvMethod,
  IdvStatus,
  SignerIdvMethodState,
  SignerIdvState,
} from "types/identityVerification";

/**
 * Shared signer identity verification (IDV) logic — pure, no React, no API.
 *
 * Every surface that touches IDV goes through here so the rules live once:
 * the signer security dropdown (compose / template on desktop + mobile, and
 * the bulk Signers step), the request-detail badge, and the signer's
 * verification screen.
 */

/** Canonical order: when both are required the signer does ID first. */
export const IDV_METHODS: IdvMethod[] = ["ID_DOCUMENT", "KBA"];

const copy = strings.identityVerification;

// ------------------------------------------------------------ sender: security option

/** De-duplicated, known methods only, in canonical order. */
export const sortIdvMethods = (methods?: IdvMethod[] | null): IdvMethod[] =>
  IDV_METHODS.filter((method) => (methods ?? []).includes(method));

/** Identity verification as one of a signer's security options. */
export type IdvSecurityKey = "idvId" | "idvKba" | "idvBoth";

export interface IdvSecurityOption {
  key: IdvSecurityKey;
  methods: IdvMethod[];
  label: string;
  caption: string;
}

/**
 * The dropdown entries, one per method combination, listed after the existing
 * options (No security, Email OTP, SMS OTP, Access Code). Single choice: an
 * IDV option replaces any code or OTP, as the backend requires.
 */
export const IDV_SECURITY_OPTIONS: IdvSecurityOption[] = (
  [
    ["idvId", ["ID_DOCUMENT"]],
    ["idvKba", ["KBA"]],
    ["idvBoth", ["ID_DOCUMENT", "KBA"]],
  ] as [IdvSecurityKey, IdvMethod[]][]
).map(([key, methods]) => ({
  key,
  methods,
  label: copy.securityOptions[key].label,
  caption: copy.securityOptions[key].caption,
}));

type WithIdvMethods = { identityVerificationMethods?: IdvMethod[] | null };

/** The methods a signer must pass, from their `requestSecurity`. */
export const signerIdvMethods = (security?: WithIdvMethods | null) =>
  sortIdvMethods(security?.identityVerificationMethods);

export const isIdvSecurityKey = (key?: string | null): key is IdvSecurityKey =>
  IDV_SECURITY_OPTIONS.some((option) => option.key === key);

/** The IDV option the signer's security matches, or null when it isn't IDV. */
export const idvSecurityKeyOf = (
  security?: WithIdvMethods | null,
): IdvSecurityKey | null => {
  const methods = signerIdvMethods(security).join();
  if (!methods) return null;
  return (
    IDV_SECURITY_OPTIONS.find((option) => option.methods.join() === methods)
      ?.key ?? null
  );
};

/** The methods an option stands for; empty for any non-IDV option. */
export const idvMethodsFor = (key?: string | null): IdvMethod[] =>
  IDV_SECURITY_OPTIONS.find((option) => option.key === key)?.methods ?? [];

export const idvSecurityOption = (key?: string | null) =>
  IDV_SECURITY_OPTIONS.find((option) => option.key === key);

/**
 * The options the dropdown shows: those whose every method a vendor runs here
 * (so "Knowledge questions" appears by itself once a KBA vendor is set), plus
 * the signer's current one even if it isn't, so it stays visible and can be
 * changed.
 */
export const idvSecurityOptionsFor = (
  availableMethods: IdvMethod[],
  currentKey?: string | null,
): IdvSecurityOption[] =>
  IDV_SECURITY_OPTIONS.filter(
    (option) =>
      option.key === currentKey ||
      option.methods.every((method) => availableMethods.includes(method)),
  );

/** Any non-CC signer whose security option is identity verification. */
export const anySignerRequiresIdv = (
  signers?: { cc?: boolean; requestSecurity?: WithIdvMethods | null }[] | null,
) =>
  (signers ?? []).some(
    (signer) => !signer.cc && signerIdvMethods(signer.requestSecurity).length > 0,
  );

/** Statuses the backend uses to refuse an IDV requirement at send / save. */
const IDV_SEND_REFUSAL_STATUSES = [400, 403, 423];

/**
 * Catch-block handler for a send / save / bulk-create where some signer's
 * security option is IDV (`usesIdv`). Then a 400 / 403 / 423 is the backend
 * explaining why it can't (no vendor, in-person signer, IDV plus a code,
 * self-sign, not on plan, switched off) in text written to be shown as-is —
 * `handleErrorMessage` would replace it with a generic line. Anything else
 * falls through to `handleErrorMessage` unchanged.
 */
export const handleIdvAwareError = (error: any, usesIdv: boolean) => {
  const message = error?.message;
  if (
    usesIdv &&
    !error?.featureBlocked &&
    !error?.permissionBlocked &&
    IDV_SEND_REFUSAL_STATUSES.includes(getApiStatus(error) ?? 0) &&
    typeof message === "string" &&
    message.trim()
  ) {
    openErrorNotification(message);
    return;
  }
  handleErrorMessage(error);
};

// ------------------------------------------------------------ sender view

export type IdvBadgeTone = "neutral" | "progress" | "pending" | "declined" | "completed";

/**
 * Request-detail badge for a signer. `NOT_REQUIRED` has no badge. Tones are
 * the request-detail screen's own status-chip keys: gray / blue / amber / red /
 * green, matching the contract's colour table.
 */
export const getIdvBadge = (
  status?: IdvStatus | null,
): { tone: IdvBadgeTone; label: string } | null => {
  switch (status) {
    case "NOT_STARTED":
      return { tone: "neutral", label: copy.senderBadges.NOT_STARTED };
    case "IN_PROGRESS":
      return { tone: "progress", label: copy.senderBadges.IN_PROGRESS };
    case "FAILED":
      return { tone: "pending", label: copy.senderBadges.FAILED };
    case "BLOCKED":
      return { tone: "declined", label: copy.senderBadges.BLOCKED };
    case "PASSED":
      return { tone: "completed", label: copy.senderBadges.PASSED };
    default:
      return null;
  }
};

// ------------------------------------------------------------ signer

/** The verification screen replaces the document until this is false. */
export const needsSignerIdv = (state?: SignerIdvState | null) =>
  !!state?.required &&
  state.status !== "PASSED" &&
  state.status !== "NOT_REQUIRED";

/**
 * A signer document call refused because the signer hasn't verified yet
 * (`403 IDV_REQUIRED: …`). Reads the message whether the caller threw the
 * parsed body (`makeCall`) or a bare string (`fetchDocument`).
 */
export const isIdvRequiredError = (error: any) =>
  getErrorMessageText(error).startsWith(strings.IDV_REQUIRED_PREFIX);

/** `409 IDV_BLOCKED: …` from starting a session with no attempts left. */
export const isIdvBlockedError = (error: any) =>
  getErrorMessageText(error).startsWith(strings.IDV_BLOCKED_PREFIX);

/** `409 IDV_ALREADY_VERIFIED: …` from starting: the signer has already passed. */
export const isIdvAlreadyVerifiedError = (error: any) =>
  getErrorMessageText(error).startsWith(strings.IDV_ALREADY_VERIFIED_PREFIX);

/** `409 IDV_STARTING: …` from starting: a double-click race; retry once. */
export const isIdvStartingError = (error: any) =>
  getErrorMessageText(error).startsWith(strings.IDV_STARTING_PREFIX);

/** The method the signer works on now: `nextMethod`, else the first not yet passed. */
export const currentIdvMethod = (
  state?: SignerIdvState | null,
): SignerIdvMethodState | undefined => {
  const methods = state?.methods ?? [];
  return (
    methods.find((m) => m.method === state?.nextMethod) ??
    methods.find((m) => m.status !== "PASSED")
  );
};

export const idvFailureHint = (reason?: string | null): string =>
  (reason && copy.failureHints[reason]) || copy.failureHints.default;

export const idvAttemptsLeftLabel = (count: number): string =>
  count === 1
    ? copy.attemptsLeftOne
    : fillCopy(copy.attemptsLeftMany, { count });
